'use client';
import { useEffect, useState } from 'react';
import { useExperience } from './Experience';
import { buildLocalizedHref } from '../../app/lib/routeUtils';
import { updateAnalytics } from '../lib/analytics';
import { consentStorageKey, legacyConsentStorageKey, normalizeConsent, readConsent, saveConsent } from '../lib/cookie-consent';
import { recordVisitEvent } from '../lib/visit-metrics';
import { useTranslation } from '../../app/hooks/useTranslation';

export function ConsentButton({ children, ...props }) { return <button {...props} type="button" onClick={() => window.dispatchEvent(new Event('palmate:consent-settings'))}>{children}</button>; }
// Two-layer consent card anchored to the bottom. It does not block the page; accepting and refusing carry equal weight.
export function CookiePreferences() {
  const { t, locale, internalPath } = useExperience(); const [open, setOpen] = useState(false);
  // Legal pages stay readable: the dialog never opens by itself there, only from the footer link.
  const legalPage = /^privacy-policy(?:\/|$)/.test(internalPath || '');
  const [view, setView] = useState('summary');
  const { t: translate } = useTranslation();
  const [preferences, setPreferences] = useState(() => normalizeConsent(false));
  useEffect(() => {
    const restore = () => {
      let saved; try { saved = readConsent(window.localStorage); } catch {}
      const consent = normalizeConsent(saved);
      setPreferences(consent); updateAnalytics(consent);
      return saved;
    };
    setOpen(!restore() && !legalPage);
    // The footer link reopens the dialog directly on the detailed view.
    const show = () => { setView('details'); setOpen(true); }; window.addEventListener('palmate:consent-settings', show);
    const sync = event => {
      if (event.key === null || event.key === consentStorageKey || event.key === legacyConsentStorageKey) {
        if (!restore() && !legalPage) { setView('summary'); setOpen(true); }
      }
    };
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener('palmate:consent-settings', show); window.removeEventListener('storage', sync); };
  }, [legalPage]);
  function choose(value) {
    const consent = normalizeConsent(value);
    try { saveConsent(window.localStorage, consent); } catch {}
    // The choice itself is counted anonymously so the consent rate is known.
    try { recordVisitEvent('consent', value === false ? 'necessary' : consent.analytics && consent.marketing ? 'all' : 'custom'); } catch {}
    setPreferences(consent); updateAnalytics(consent); setOpen(false); setView('summary');
  }
  if (!open) return null;
  const acceptAll = <button type="button" data-analytics-choice="all" onClick={() => choose({ analytics: true, marketing: true })}>{translate('component.cookiePreferences.acceptAll')}</button>;
  const intro = <p>{translate('component.cookiePreferences.description')} <a href={buildLocalizedHref(locale, 'privacy-policy')} target="_blank" rel="noopener">{t('Gizlilik bilgileri')}</a></p>;
  return <div className="consent-overlay">
    <div className="analytics-consent" role="dialog" aria-labelledby="consent-dialog-title" data-view={view}>
      <h2 id="consent-dialog-title">{translate('component.cookiePreferences.title')}</h2>
      {intro}
      {view === 'details' && <fieldset className="consent-options">
        <legend>{t('Çerez tercihleri')}</legend>
        <label className="consent-option is-locked">
          <input type="checkbox" checked disabled readOnly/>
          <span><strong>{translate('component.cookiePreferences.necessary')}</strong><small>{translate('component.cookiePreferences.necessaryDescription')}</small><small className="consent-providers">{translate('component.cookiePreferences.necessaryScope')}</small></span>
        </label>
        <label className="consent-option">
          <input type="checkbox" checked={preferences.analytics} onChange={event => setPreferences(current => ({ ...current, analytics: event.target.checked }))}/>
          <span><strong>{translate('component.cookiePreferences.analytics')}</strong><small>{t('Site kullanımını anlamak için isteğe bağlı analiz çerezleri kullanıyoruz.')}</small><small className="consent-providers">{translate('component.cookiePreferences.analyticsScope')}</small></span>
        </label>
        <label className="consent-option">
          <input type="checkbox" checked={preferences.marketing} onChange={event => setPreferences(current => ({ ...current, marketing: event.target.checked }))}/>
          <span><strong>{translate('component.cookiePreferences.marketing')}</strong><small>{translate('component.cookiePreferences.marketingDescription')}</small><small className="consent-providers">{translate('component.cookiePreferences.marketingScope')}</small></span>
        </label>
      </fieldset>}
      {view === 'details' && <p className="consent-list-link">{translate('component.cookiePreferences.fullList')} <a href={buildLocalizedHref(locale, 'privacy-policy') + '?tab=cookie-policy'} target="_blank" rel="noopener">{translate('component.cookiePreferences.cookiePolicy')}</a></p>}
      {view === 'summary'
        ? <div className="consent-actions">{acceptAll}<button type="button" data-analytics-choice="necessary" onClick={() => choose(false)}>{t('Yalnızca gerekli')}</button></div>
        : <div className="consent-actions"><button type="button" data-analytics-choice="granted" onClick={() => choose(preferences)}>{translate('component.cookiePreferences.save')}</button>{acceptAll}</div>}
      {view === 'summary'
        ? <button type="button" className="consent-link" onClick={() => setView('details')}>{translate('component.cookiePreferences.manage')}</button>
        : <button type="button" className="consent-link" onClick={() => setView('summary')}>{translate('component.cookiePreferences.back')}</button>}
      <p className="consent-note">{translate('component.cookiePreferences.changeLater')}</p>
    </div>
  </div>;
}
